import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import AdminLayout from '../components/AdminLayout';
import { 
  CogIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import toast from '../utils/toast';

const AdminSystemSettings = () => {
  const { admin } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState([]);
  const [editingSetting, setEditingSetting] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await api.getAdminSystemSettings(token);
      
      // Ensure we have an array of settings
      const settingsData = response.data.settings;
      if (Array.isArray(settingsData)) {
        setSettings(settingsData);
      } else {
        console.warn('Settings data is not an array:', settingsData);
        setSettings([]);
        toast.error('Invalid settings data format');
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load system settings');
      setSettings([]); // Ensure it's always an array
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (setting) => {
    setEditingSetting(setting.key);
    // Parse JSON value for display
    try {
      const parsedValue = JSON.parse(setting.value);
      setEditValue(typeof parsedValue === 'string' ? parsedValue : JSON.stringify(parsedValue));
    } catch {
      setEditValue(setting.value);
    }
  };

  const handleSave = async (key) => {
    if (admin && admin.role !== "super_admin") {
      toast.error('You do not have permission to perform this action');
      return;
    }
    try {
      setSaving(true);
      const token = localStorage.getItem('admin_token');
      
      // Try to parse as JSON, fallback to string
      let valueToSave;
      try {
        // Check if it's a boolean
        if (editValue === 'true' || editValue === 'false') {
          valueToSave = editValue === 'true';
        } else if (!isNaN(editValue) && editValue.trim() !== '') {
          // Check if it's a number
          valueToSave = parseFloat(editValue);
        } else {
          // Treat as string
          valueToSave = editValue;
        }
      } catch {
        valueToSave = editValue;
      }

      await api.updateSystemSetting(token, key, valueToSave);
      toast.success('Setting updated successfully');
      
      // Refresh settings
      await fetchSettings();
      setEditingSetting(null);
      setEditValue('');
    } catch (error) {
      console.error('Failed to update setting:', error);
      toast.error('Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingSetting(null);
    setEditValue('');
  };

  const getValueBadge = (value) => {
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === 'boolean') {
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            parsed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {parsed ? 'Enabled' : 'Disabled'}
          </span>
        );
      }
      if (typeof parsed === 'number') {
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {parsed}
          </span>
        );
      }
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          {parsed}
        </span>
      );
    } catch {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          {value}
        </span>
      );
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="bg-gray-50 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-whatsapp-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading system settings...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center">
              <CogIcon className="h-8 w-8 text-whatsapp-600 mr-3" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">System Settings</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Configure system-wide settings and preferences
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Warning Banner */}
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  <strong>Warning:</strong> Changing these settings may affect system functionality. 
                  Please be careful when modifying values.
                </p>
              </div>
            </div>
          </div>

          {/* Settings Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Configuration Settings</h3>
              <p className="text-sm text-gray-500 mt-1">
                Manage system configuration values
              </p>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Setting
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Value
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Updated
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {settings.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-8 text-center">
                        <CogIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-4 text-lg font-medium text-gray-900">No settings found</h3>
                        <p className="mt-2 text-gray-600">No system settings are configured.</p>
                      </td>
                    </tr>
                  ) : (
                    Array.isArray(settings) ? settings.map((setting) => (
                      <tr key={setting.key} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {setting.key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </div>
                            {setting.description && (
                              <div className="text-sm text-gray-500 mt-1">
                                {setting.description}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {editingSetting === setting.key ? (
                            <div className="flex items-center space-x-2">
                              <input
                                type="text"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-whatsapp-500 focus:border-whatsapp-500"
                                placeholder="Enter value..."
                              />
                              <button
                                onClick={() => handleSave(setting.key)}
                                disabled={saving}
                                className="p-2 text-green-600 hover:text-green-800 disabled:opacity-50"
                              >
                                <CheckIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={handleCancel}
                                disabled={saving}
                                className="p-2 text-red-600 hover:text-red-800 disabled:opacity-50"
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            getValueBadge(setting.value)
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatDate(setting.updated_at)}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-medium">
                          {editingSetting !== setting.key && (
                            <button
                              onClick={() => handleEdit(setting)}
                              className="text-whatsapp-600 hover:text-whatsapp-800"
                            >
                              <PencilIcon className="h-4 w-4 inline mr-1" />
                              Edit
                            </button>
                          )}
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="4" className="px-6 py-8 text-center text-red-600">
                          Error: Invalid settings data format
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Info Box */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <InformationCircleIcon className="h-5 w-5 text-blue-400" />
              <div className="ml-3">
                <h4 className="text-sm font-medium text-blue-800">Setting Types</h4>
                <div className="mt-2 text-sm text-blue-700">
                  <ul className="list-disc list-inside space-y-1">
                    <li><strong>Boolean:</strong> Use "true" or "false" for yes/no settings</li>
                    <li><strong>Number:</strong> Enter numeric values for limits and counts</li>
                    <li><strong>Text:</strong> Enter text values for descriptions and names</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSystemSettings;
